import { useState } from 'react';
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function NafahLogin({ onSuccess }) {
  const [step, setStep] = useState('id'); // id, verification, loading
  const [nationalId, setNationalId] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [randomNumbers, setRandomNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleIdSubmit = (e) => {
    e.preventDefault();
    if (nationalId.length === 10 && consentChecked) {
      setIsLoading(true);
      // Generate 3 random 2-digit numbers
      const numbers = Array.from({ length: 3 }, () => Math.floor(Math.random() * 90) + 10);
      setTimeout(() => {
        setRandomNumbers(numbers);
        setIsLoading(false);
        setStep('verification');
      }, 1500);
    }
  };

  const handleNumberSelect = () => {
    setIsLoading(true);
    setTimeout(() => {
      // Return basic user data - full name from Nafath
      // firstName is extracted for welcome screen
      const fullName = 'فيصل سعود الخميسي';
      onSuccess({
        nationalId: nationalId,
        fullName: fullName,
        firstName: fullName.split(' ')[0], // Extract first name
      });
    }, 1500);
  };

  const isIdFormValid = nationalId.length === 10 && consentChecked;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <Card className="w-full max-w-md p-8 border-0 shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg p-3">
            <img
              src="/images/logos/nafath-login.png"
              alt="Nafath"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">تسجيل الدخول عبر نفاذ</h1>
          <p className="text-slate-500">النفاذ الوطني الموحد</p>
        </div>

        {step === 'id' && (
          <form onSubmit={handleIdSubmit} className="space-y-6">
            {/* Consent Checkbox */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(checked === true)}
                  className="mt-1 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                />
                <Label
                  htmlFor="consent"
                  className="text-sm text-amber-900 leading-relaxed cursor-pointer"
                >
                  أوافق على مشاركة جميع معلوماتي الشخصية والمالية لغرض إتمام عملية شراء هذا العقار
                </Label>
              </div>
            </div>

            {/* National ID Input */}
            <div>
              <Label className="text-slate-700 mb-2 block">رقم الهوية الوطنية</Label>
              <Input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="أدخل رقم الهوية"
                className="h-14 text-lg text-center border-slate-200 focus:border-emerald-500"
                dir="ltr"
              />
            </div>

            <Button
              type="submit"
              disabled={!isIdFormValid || isLoading}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'متابعة'
              )}
            </Button>

            {!consentChecked && nationalId.length === 10 && (
              <p className="text-sm text-amber-600 text-center">
                يرجى الموافقة على الشروط للمتابعة
              </p>
            )}
          </form>
        )}

        {step === 'verification' && (
          <div className="space-y-6">
            <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#00A651"/>
                  <path d="M8 12l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-900 mb-1">
                  افتح تطبيق نفاذ
                </p>
                <p className="text-sm text-emerald-700">
                  اختر الرقم الذي تراه في تطبيق نفاذ
                </p>
              </div>
            </div>
            <div>
              <Label className="text-slate-700 mb-3 block">اختر الرقم</Label>
              <div className="grid grid-cols-3 gap-3">
                {randomNumbers.map((number, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleNumberSelect(number)}
                    disabled={isLoading}
                    className="h-20 text-3xl font-bold rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-slate-300 focus:ring-0"
                  >
                    {number}
                  </button>
                ))}
              </div>
            </div>
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          محمي بواسطة النفاذ الوطني الموحد
        </p>
      </Card>
    </motion.div>
  );
}
